import { memo, useCallback, useEffect, useRef } from 'react'

import { ShowcaseCollection } from '@nativewaves/nw-cloud-sdk/showcase'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'

import { Editor, EditorInstance } from '@shared/components/Editor'
import { LoadingIndicator, NotFound } from '@shared/layouts'
import { useCollectionUpdateMutation } from 'hooks/mutations/showcase/Collection'
import { rawRoute } from 'pages/NWPlatform/Collections/Collection'
import { collectionQueryKeys } from 'services/queryKeys'

const JSONRaw = () => {
  const { t } = useTranslation(['common', 'domain'])
  const { enqueueSnackbar } = useSnackbar()

  const editorInstanceRef = useRef<EditorInstance>(undefined)

  const queryClient = useQueryClient()

  const { environmentId, collectionId } = rawRoute.useParams()

  const collectionQuery = useQuery({
    ...collectionQueryKeys.detail({ collectionId }),
    initialData: queryClient
      .getQueryData(collectionQueryKeys.list({ environmentId }).queryKey)
      ?.pages.flatMap((page) => page.items)
      .find((item) => item.id === collectionId),
  })
  const { data: collection } = collectionQuery

  const { mutate: updateCollection } = useCollectionUpdateMutation()

  const handleFormSubmit = useCallback(
    (data: ShowcaseCollection) => {
      updateCollection(
        { collectionId, showcaseCollectionEdit: data as object },
        {
          onSuccess: () => {
            enqueueSnackbar(t('domain:Showcase.Collection.collectionSaved'), {
              variant: 'success',
            })
          },
        },
      )
    },
    [collectionId, enqueueSnackbar, t, updateCollection],
  )

  useEffect(() => {
    if (editorInstanceRef.current && collectionQuery.isSuccess) {
      editorInstanceRef.current?.getModel()?.setValue(window.JSON.stringify(collection, null, 4))
    }
  }, [collectionQuery.isSuccess, collection])

  if (collectionQuery.isLoading) {
    return <LoadingIndicator />
  }

  if (!collection) {
    return <NotFound />
  }

  return (
    <Editor
      value={collection}
      onSubmit={handleFormSubmit}
      getEditorInstance={(editorInstance: EditorInstance) => (editorInstanceRef.current = editorInstance)}
    />
  )
}

export const JSON = memo(JSONRaw)
